import { useMemo, useState } from "react";
import { Card } from "antd";
import { toast } from "react-hot-toast";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";

const splitWords = (raw) => {
  if (!raw) return [];
  const spaced = raw
    .replace(/([\p{Ll}\p{N}])(\p{Lu})/gu, "$1 $2")
    .replace(/(\p{Lu}+)(\p{Lu}\p{Ll})/gu, "$1 $2");
  return spaced
    .split(/[\s\-_./\\,:;|]+/u)
    .map((w) => w.replace(/[^\p{L}\p{N}]+/gu, ""))
    .filter(Boolean);
};

const capitalize = (w) =>
  w.length === 0 ? w : w.charAt(0).toLocaleUpperCase() + w.slice(1).toLocaleLowerCase();

const toCamel = (words) =>
  words
    .map((w, i) => (i === 0 ? w.toLocaleLowerCase() : capitalize(w)))
    .join("");

const toPascal = (words) => words.map(capitalize).join("");
const toSnake = (words) => words.map((w) => w.toLocaleLowerCase()).join("_");
const toScreamingSnake = (words) =>
  words.map((w) => w.toLocaleUpperCase()).join("_");
const toKebab = (words) => words.map((w) => w.toLocaleLowerCase()).join("-");
const toCobol = (words) => words.map((w) => w.toLocaleUpperCase()).join("-");
const toTrain = (words) => words.map(capitalize).join("-");
const toTitle = (words) => words.map(capitalize).join(" ");
const toSentence = (words) => {
  if (words.length === 0) return "";
  const [first, ...rest] = words;
  return [capitalize(first), ...rest.map((w) => w.toLocaleLowerCase())].join(
    " "
  );
};
const toDot = (words) => words.map((w) => w.toLocaleLowerCase()).join(".");
const toPath = (words) => words.map((w) => w.toLocaleLowerCase()).join("/");

const DEFAULT_INPUT = "Quick Tools for Day-to-Day Developer Use";

const CaseConverter = () => {
  const { t } = useTranslation();
  const [input, setInput] = useState(DEFAULT_INPUT);

  const words = useMemo(() => splitWords(input), [input]);

  const outputs = useMemo(() => {
    const upper = input.toLocaleUpperCase();
    const lower = input.toLocaleLowerCase();
    return [
      { key: "camel", labelKey: "case_converter_camel", value: toCamel(words) },
      { key: "pascal", labelKey: "case_converter_pascal", value: toPascal(words) },
      { key: "snake", labelKey: "case_converter_snake", value: toSnake(words) },
      {
        key: "screaming_snake",
        labelKey: "case_converter_screaming_snake",
        value: toScreamingSnake(words),
      },
      { key: "kebab", labelKey: "case_converter_kebab", value: toKebab(words) },
      { key: "train", labelKey: "case_converter_train", value: toTrain(words) },
      { key: "cobol", labelKey: "case_converter_cobol", value: toCobol(words) },
      { key: "title", labelKey: "case_converter_title_case", value: toTitle(words) },
      {
        key: "sentence",
        labelKey: "case_converter_sentence",
        value: toSentence(words),
      },
      { key: "upper", labelKey: "case_converter_upper", value: upper },
      { key: "lower", labelKey: "case_converter_lower", value: lower },
      { key: "dot", labelKey: "case_converter_dot", value: toDot(words) },
      { key: "path", labelKey: "case_converter_path", value: toPath(words) },
    ];
  }, [input, words]);

  const copy = async (text, label) => {
    if (!text) {
      toast.error(t("case_converter_nothing_to_copy"));
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} ${t("copy_success")}`);
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const copyAll = async () => {
    const rows = outputs.filter((r) => r.value);
    if (rows.length === 0) {
      toast.error(t("case_converter_nothing_to_copy"));
      return;
    }
    const text = rows.map((r) => `${t(r.labelKey)}\t${r.value}`).join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t("case_converter_copy_all_success"));
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto p-4">
        <Card className="shadow-lg rounded-xl">
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-2xl font-bold text-indigo-700">
              {t("case_converter_title")}
            </h1>
            <p className="text-gray-600">{t("case_converter_intro")}</p>

            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <label className="text-sm font-medium text-gray-700">
                  {t("case_converter_input_label")}
                </label>
                <div className="flex items-center gap-2">
                  <StandardButton
                    onClick={() => setInput(DEFAULT_INPUT)}
                    type="secondary"
                    variant="outlined"
                    size="small"
                    disabled={input === DEFAULT_INPUT}
                  >
                    {t("case_converter_load_example")}
                  </StandardButton>
                  <StandardButton
                    onClick={() => setInput("")}
                    type="secondary"
                    variant="outlined"
                    size="small"
                    disabled={!input}
                  >
                    {t("case_converter_clear")}
                  </StandardButton>
                </div>
              </div>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t("case_converter_placeholder")}
                rows={4}
                className="w-full p-2 font-mono border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:outline-none resize-y"
              />
              <div className="text-xs text-gray-500">
                {t("case_converter_words_detected")}: {words.length}
              </div>
            </div>

            <div className="bg-indigo-50 p-4 rounded-md border border-indigo-200">
              <div className="flex justify-end mb-2">
                <StandardButton
                  onClick={copyAll}
                  type="primary"
                  variant="outlined"
                  size="small"
                  disabled={!outputs.some((r) => r.value)}
                >
                  {t("case_converter_copy_all")}
                </StandardButton>
              </div>
              {words.length === 0 ? (
                <div className="text-sm text-gray-600 bg-white border border-indigo-100 rounded-md p-4 text-center">
                  {t("case_converter_empty_state")}
                </div>
              ) : (
                <ul className="flex flex-col divide-y divide-indigo-100">
                  {outputs.map((row) => (
                    <li
                      key={row.key}
                      className="flex items-center justify-between gap-3 py-2"
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <span className="inline-block w-40 shrink-0 text-xs font-semibold text-indigo-700 uppercase">
                          {t(row.labelKey)}
                        </span>
                        <span className="font-mono text-sm break-all text-gray-800">
                          {row.value || (
                            <em className="text-gray-400 not-italic">—</em>
                          )}
                        </span>
                      </div>
                      <StandardButton
                        onClick={() => copy(row.value, t(row.labelKey))}
                        type="secondary"
                        variant="outlined"
                        size="small"
                        disabled={!row.value}
                      >
                        {t("copy_button")}
                      </StandardButton>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="text-xs text-gray-500 leading-relaxed">
              <p>{t("case_converter_note_splitting")}</p>
              <p>{t("case_converter_note_unicode")}</p>
              <p>{t("case_converter_note_privacy")}</p>
            </div>
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default CaseConverter;
